import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftRight,
  CircleHelp,
  FileText,
  Gavel,
  LogOut,
  Mic2,
  Monitor,
  ScrollText,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  Vote,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useModuleAccess } from '../auth/useModuleAccess';
import { hasSupabaseConfig } from '../lib/supabaseClient';
import { ensureOrgMeeting } from '../db/sync/membersSync';
import { clearMeetingSessionFlag } from '../utils/meetingSession';
import { getLiveMeetingSession, ensureLiveMeetingSession, endMeetingSession, officerSessionUrl } from '../utils/officerSession';
import {
  archiveAndEndLiveSession,
  archivePayloadFromSessionState,
  createAdjournmentMinuteEntry,
  minutesIncludeAdjournment,
} from '../utils/finalizeMeeting';
import { useTours } from '../tours/TourProvider';
import { formatSupabaseError } from '../lib/supabaseErrors';
import SignedInUserLine from '../components/common/SignedInUserLine';

export default function OrgHomePage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { memberships, switchOrganization, signOut, activeOrganizationId, ready, user, isPlatformAdmin } =
    useAuth();
  const { canMeeting, canCommittees, canElections, canAdmin, role } = useModuleAccess();
  const { openCatalog, shouldAutoOpenCatalog, tourBusy } = useTours();
  const autoOpenedRef = useRef(false);
  const [liveSession, setLiveSession] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [endBusy, setEndBusy] = useState(false);
  const [liveError, setLiveError] = useState(null);

  useEffect(() => {
    if (!ready) return;
    if (!hasSupabaseConfig()) {
      navigate('/', { replace: true });
      return;
    }
    if (!orgId) return;
    const member = memberships.find((m) => m.organization_id === orgId);
    if (!member) {
      navigate('/orgs', { replace: true });
      return;
    }
    if (activeOrganizationId !== orgId) {
      void switchOrganization(orgId);
    }
  }, [ready, orgId, memberships, activeOrganizationId, switchOrganization, navigate]);

  useEffect(() => {
    if (!ready || !orgId) return;
    const member = memberships.find((m) => m.organization_id === orgId);
    if (!member) return;
    if (autoOpenedRef.current) return;
    if (!shouldAutoOpenCatalog()) return;
    autoOpenedRef.current = true;
    openCatalog();
  }, [ready, orgId, memberships, user?.id, openCatalog, shouldAutoOpenCatalog]);

  useEffect(() => {
    if (!ready || !orgId || !canMeeting || !hasSupabaseConfig()) {
      setLiveSession(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const live = await getLiveMeetingSession(orgId);
        if (!cancelled) setLiveSession(live);
      } catch (err) {
        console.warn('[OrgHome] getLiveMeetingSession failed', err?.message || err);
        if (!cancelled) setLiveSession(null);
      }
    };
    void load();
    const id = setInterval(load, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [ready, orgId, canMeeting]);

  const membership = memberships.find((m) => m.organization_id === orgId);
  const orgName = membership?.organization_name || 'Organization';

  const startMeeting = async () => {
    if (!canMeeting || !orgId) return;
    clearMeetingSessionFlag();
    setLiveBusy(true);
    try {
      const meeting = await ensureOrgMeeting(orgId);
      const live = await ensureLiveMeetingSession(orgId, meeting?.id || null);
      const url = officerSessionUrl({
        orgId,
        sessionId: live?.id,
        seat: 'driver',
        origin: '',
      });
      navigate(url.replace(/^\//, '/') || `/orgs/${orgId}/meeting?session=${live?.id || ''}`);
    } catch (err) {
      console.warn('[OrgHome] start meeting session failed', err?.message || err);
      navigate(`/orgs/${orgId}/meeting`);
    } finally {
      setLiveBusy(false);
    }
  };

  const joinLive = (seat) => {
    if (!liveSession?.id || !orgId) return;
    const path = officerSessionUrl({
      orgId,
      sessionId: liveSession.id,
      seat,
      origin: '',
    });
    navigate(path.startsWith('http') ? path.replace(window.location.origin, '') : path);
  };

  const endLiveMeeting = async () => {
    if (!liveSession?.id) return;

    const state = liveSession.state || {};
    const minutes = Array.isArray(state.meetingMinutes) ? state.meetingMinutes : [];
    const hasAdjourn = minutesIncludeAdjournment(minutes);
    let recordAdjournment = false;

    if (!hasAdjourn && minutes.length > 0) {
      const record = window.confirm(
        'Record adjournment in the minutes, then end the session?\n\nOK = add adjournment line and save to Meeting Log.\nCancel = end/archive without adding adjournment.'
      );
      if (record) {
        recordAdjournment = true;
      } else if (
        !window.confirm(
          'End the live officer session without recording adjournment? Minutes will still be saved to the Meeting Log when available.'
        )
      ) {
        return;
      }
    } else if (
      !window.confirm(
        'End the live officer session? Minutes will be saved to the Meeting Log when available. Everyone will be disconnected from this meeting room.'
      )
    ) {
      return;
    }

    setEndBusy(true);
    setLiveError(null);
    try {
      let payload = archivePayloadFromSessionState(state, {
        organizationId: orgId,
        meetingSessionId: liveSession.id,
      });

      if (recordAdjournment && !minutesIncludeAdjournment(payload.meetingMinutes)) {
        const entry = createAdjournmentMinuteEntry({
          meetingStartTime: payload.meetingStartTime,
        });
        payload = {
          ...payload,
          meetingMinutes: [...payload.meetingMinutes, entry],
          meetingEndTime: entry.metadata.fullDateTime,
        };
      }

      const { archiveError, endedSession } = await archiveAndEndLiveSession({
        ...payload,
        endSession: true,
      });

      if (archiveError) {
        const msg = formatSupabaseError(
          archiveError,
          'Could not save minutes to the Meeting Log.'
        );
        if (
          !window.confirm(`${msg}\n\nEnd the live session anyway? Unsaved minutes may be lost.`)
        ) {
          return;
        }
        if (!endedSession) {
          await endMeetingSession(liveSession.id);
        }
      }

      clearMeetingSessionFlag();
      setLiveSession(null);
    } catch (err) {
      setLiveError(formatSupabaseError(err, 'Could not end live meeting'));
    } finally {
      setEndBusy(false);
    }
  };

  const tiles = [
    {
      id: 'meeting',
      label: 'Start meeting',
      description: 'Chair console, motions, and live minutes',
      icon: Gavel,
      allowed: canMeeting,
      onClick: () => void startMeeting(),
      tourId: 'tile-meeting',
    },
    {
      id: 'archives',
      label: 'Meeting Log',
      description: 'Past meetings and re-export minutes',
      icon: ScrollText,
      allowed: canMeeting || canAdmin,
      onClick: () => navigate(`/orgs/${orgId}/archives`),
      tourId: 'tile-archives',
    },
    {
      id: 'committees',
      label: 'Committees',
      description: 'Standing committees and referrals',
      icon: FileText,
      allowed: canCommittees,
      onClick: () => navigate(`/orgs/${orgId}/committees`),
      tourId: 'tile-committees',
    },
    {
      id: 'elections',
      label: 'Elections',
      description: 'Nominations and balloting',
      icon: Vote,
      allowed: canElections,
      onClick: () => navigate(`/orgs/${orgId}/elections`),
      tourId: 'tile-elections',
    },
    {
      id: 'admin',
      label: 'Users',
      description: 'Invite logins and manage module access',
      icon: Shield,
      allowed: canAdmin,
      onClick: () => navigate(`/orgs/${orgId}/admin`),
      tourId: 'tile-admin',
    },
    {
      id: 'roster',
      label: 'Member roster',
      description: 'Names for roll call and committees',
      icon: Users,
      allowed: canAdmin,
      onClick: () => navigate(`/orgs/${orgId}/roster`),
      tourId: 'tile-roster',
    },
    {
      id: 'settings',
      label: 'Organization settings',
      description: 'Default agenda, consent calendar, and UC requests',
      icon: SlidersHorizontal,
      allowed: canAdmin,
      onClick: () => navigate(`/orgs/${orgId}/settings`),
      tourId: 'tile-settings',
    },
  ];

  return (
    <div className="min-h-screen bg-pp-paper text-pp-ink">
      <header className="border-b border-pp-line/80 bg-pp-surface/90 backdrop-blur-md" data-tour="org-home-header">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-pp-muted">Organization</p>
            <h1 className="text-xl font-semibold tracking-tight">{orgName}</h1>
            {role && <p className="text-xs text-pp-muted">Your role: {role}</p>}
            <SignedInUserLine className="mt-0.5" />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-tour="help-tours"
              onClick={() => openCatalog()}
              className="inline-flex items-center gap-1.5 rounded-xl border border-pp-line px-3 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
              title="Guided tours"
            >
              <CircleHelp className="h-4 w-4" />
              <span className="hidden sm:inline">Help</span>
            </button>
            <Link
              to="/orgs"
              className="inline-flex items-center gap-1.5 rounded-xl border border-pp-line px-3 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
              title="Switch organization"
            >
              <ArrowLeftRight className="h-4 w-4" />
              <span className="hidden sm:inline">Switch</span>
            </Link>
            {isPlatformAdmin ? (
              <Link
                to="/platform"
                className="inline-flex items-center gap-1.5 rounded-xl border border-pp-accent/30 bg-pp-accentSoft px-3 py-2 text-sm font-semibold text-pp-accent hover:border-pp-accent/60"
                title="Platform console"
              >
                <ShieldCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Platform</span>
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => void signOut()}
              className="inline-flex items-center gap-1.5 rounded-xl border border-pp-line px-3 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center gap-2 text-sm text-pp-muted">
          <Settings className="h-4 w-4" />
          Choose a workspace module. Access is controlled by your organization admin.
        </div>

        {canMeeting && liveSession?.id && !tourBusy && (
          <section
            className="mb-6 rounded-2xl border border-pp-sage/40 bg-pp-sageSoft/40 p-4 shadow-pp"
            data-tour="live-officer-session"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-pp-sage">Live meeting</p>
            <p className="mt-1 text-sm text-pp-ink">
              An officer session is in progress. Join on this device (signed-in). Members still vote via
              QR — no login required.
            </p>
            {liveError && <p className="mt-2 text-sm text-rose-600">{liveError}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => joinLive('driver')}
                className="inline-flex items-center gap-1.5 rounded-xl bg-pp-accent px-3 py-2 text-sm font-semibold text-white"
              >
                <Gavel className="h-4 w-4" />
                Join as Driver
              </button>
              <button
                type="button"
                onClick={() => joinLive('prompt')}
                className="inline-flex items-center gap-1.5 rounded-xl border border-pp-sage/50 bg-white px-3 py-2 text-sm font-semibold text-pp-sage"
              >
                <Mic2 className="h-4 w-4" />
                Join as Chair Dashboard
              </button>
              <button
                type="button"
                onClick={() => joinLive('observer')}
                className="inline-flex items-center gap-1.5 rounded-xl border border-pp-line bg-pp-surface px-3 py-2 text-sm font-semibold text-pp-ink"
              >
                <Monitor className="h-4 w-4" />
                Join as Observer
              </button>
              <button
                type="button"
                data-tour="end-live-meeting"
                disabled={endBusy}
                onClick={() => void endLiveMeeting()}
                className="inline-flex items-center gap-1.5 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800 disabled:opacity-50"
              >
                {endBusy ? 'Ending…' : 'End live meeting'}
              </button>
            </div>
          </section>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {tiles
            .filter((t) => t.id === 'meeting' || t.id === 'archives' || t.id === 'committees' || t.id === 'elections')
            .map((tile) => {
              const Icon = tile.icon;
              return (
                <button
                  key={tile.id}
                  type="button"
                  data-tour={tile.tourId}
                  disabled={!tile.allowed || (tile.id === 'meeting' && liveBusy)}
                  onClick={tile.onClick}
                  className={`rounded-2xl border p-5 text-left transition ${
                    tile.allowed
                      ? 'border-pp-line bg-pp-surface shadow-pp hover:border-pp-accent/40'
                      : 'cursor-not-allowed border-pp-line/50 bg-pp-paper/50 opacity-50'
                  }`}
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-pp-accent/10 text-pp-accent">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="font-semibold text-pp-ink">
                    {tile.id === 'meeting' && liveBusy ? 'Starting…' : tile.label}
                  </p>
                  <p className="mt-1 text-sm text-pp-muted">{tile.description}</p>
                  {!tile.allowed && (
                    <p className="mt-2 text-xs font-medium text-rose-600">No access for your role</p>
                  )}
                </button>
              );
            })}
        </div>

        {canAdmin && (
          <div className="mt-10">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-pp-muted">
              Admin
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {tiles
                .filter((t) => t.id === 'admin' || t.id === 'roster' || t.id === 'settings')
                .map((tile) => {
                  const Icon = tile.icon;
                  return (
                    <button
                      key={tile.id}
                      type="button"
                      data-tour={tile.tourId}
                      disabled={!tile.allowed}
                      onClick={tile.onClick}
                      className={`rounded-2xl border p-5 text-left transition ${
                        tile.allowed
                          ? 'border-pp-line bg-pp-surface shadow-pp hover:border-pp-accent/40'
                          : 'cursor-not-allowed border-pp-line/50 bg-pp-paper/50 opacity-50'
                      }`}
                    >
                      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-pp-accent/10 text-pp-accent">
                        <Icon className="h-5 w-5" />
                      </div>
                      <p className="font-semibold text-pp-ink">{tile.label}</p>
                      <p className="mt-1 text-sm text-pp-muted">{tile.description}</p>
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
